const SECONDS_PER_UNIT: Record<string, number> = {
  s: 1,
  sec: 1,
  second: 1,
  m: 60,
  min: 60,
  minute: 60,
  h: 3600,
  hour: 3600,
  d: 86400,
  day: 86400,
  w: 604800,
  week: 604800,
};

const ISO_DURATION_REGEX =
  /^P(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i;

// Matches "7.days", "7 days", "1.second", "30d", "72 hours"
const UNIT_DURATION_REGEX = /^(\d+(?:\.\d+)?)[\s.]*([a-z]+?)s?$/i;

/**
 * Converts a `Duration` scalar coming from the API into a number of seconds.
 * Supports ISO-8601 (`P3D`, `PT72H`), unit strings (`7.days`, `3 days`)
 * and plain numbers (treated as seconds).
 * Returns `undefined` when the value can't be parsed.
 */
export const parseDurationToSeconds = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return Number(trimmed);
  }

  const iso = ISO_DURATION_REGEX.exec(trimmed);
  if (iso && trimmed.length > 1 && trimmed.toUpperCase() !== 'PT') {
    const [, weeks, days, hours, minutes, seconds] = iso.map(Number);
    return (
      (weeks || 0) * SECONDS_PER_UNIT.week +
      (days || 0) * SECONDS_PER_UNIT.day +
      (hours || 0) * SECONDS_PER_UNIT.hour +
      (minutes || 0) * SECONDS_PER_UNIT.minute +
      (seconds || 0)
    );
  }

  const unit = UNIT_DURATION_REGEX.exec(trimmed);
  if (unit) {
    const multiplier = SECONDS_PER_UNIT[unit[2].toLowerCase()];
    return multiplier ? Number(unit[1]) * multiplier : undefined;
  }

  return undefined;
};

/**
 * Formats seconds as the `<amount>.<unit>` string the challenge reward
 * inputs expect, e.g. `1.second`, `7.days`.
 */
export const secondsToUnitDurationString = (seconds: number): string => {
  const units: [string, number][] = [
    ['day', SECONDS_PER_UNIT.day],
    ['hour', SECONDS_PER_UNIT.hour],
    ['minute', SECONDS_PER_UNIT.minute],
  ];

  const [unitName, unitSeconds] = units.find(
    ([, size]) => seconds >= size && seconds % size === 0,
  ) || ['second', 1];

  const amount = seconds / unitSeconds;

  return `${amount}.${unitName}${amount === 1 ? '' : 's'}`;
};
